import { initializeApp, FirebaseApp } from 'firebase/app';
import { initializeAuth, connectAuthEmulator, Auth, getAuth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, Functions } from 'firebase/functions';
import { getAnalytics, Analytics } from 'firebase/analytics';
import { env } from '../config/env';
import { logger } from '../utils/logger';

logger.info('Initializing Firebase...');

let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let functions: Functions;
let analytics: Analytics | null;

try {
  // Initialize Firebase with validated configuration
  app = initializeApp(env.firebase);
  logger.info('Firebase app initialized successfully');

  // Initialize Firebase auth - for React Native/Expo, use getAuth which handles persistence automatically
  auth = getAuth(app);
  logger.info('Auth initialized');

  db = getFirestore(app);
  logger.info('Firestore initialized');

  storage = getStorage(app);
  logger.info('Storage initialized');

  functions = getFunctions(app);
  logger.info('Functions initialized');

  // Analytics initialization - wrap in try/catch as it may fail on some mobile browsers
  try {
    analytics =
      env.features.enableAnalytics && env.firebase.measurementId ? getAnalytics(app) : null;
    logger.info(`Analytics: ${analytics ? 'enabled' : 'disabled'}`);
  } catch (analyticsError) {
    logger.warn('Analytics initialization failed (non-critical):', analyticsError instanceof Error ? analyticsError : new Error(String(analyticsError)));
    analytics = null;
  }

  // Development emulator connections
  if (env.features.debugMode && process.env.EXPO_PUBLIC_USE_FIREBASE_EMULATORS === 'true') {
    logger.info('Connecting to emulators...');
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectFunctionsEmulator(functions, 'localhost', 5001);
  }
} catch (error) {
  logger.error('Failed to initialize Firebase', error instanceof Error ? error : new Error(String(error)));
  throw error;
}

export { auth, db, storage, functions, analytics };
export default app;
